/* eslint-disable no-console */

"use client"

import clsx from "clsx"
import Image from "next/image"
import React, { useState } from "react"

import BioModalTrigger from "../../leadership/BioModalTrigger"
import Pills from "../../Pills"

export interface AgendaProps {
  data: {
    time: string
    description?: string
    title?: string
    speakersCollection?: {
      items?: {
        name: string
        title: string
        biography: string
        linkedIn: string
        company: string
        bioImage?: {
          url: string
        }
      }[]
    }
    categories?: string[]
  }
}

const AgendaExpand: React.FC<AgendaProps> = ({ data }) => {
  const [toggleOpen, setToggleOpen] = useState(false)

  const getBackgroundColor = (cat) => {
    switch (cat) {
      case "Industry Trends":
        return "border-swell-orange text-swell-orange"
      case "Future of Crypto":
        return "border-swell-purple text-swell-purple"
      case "Impact":
        return "border-swell-purple text-swell-purple"
      case "Payments":
        return "border-newblue-30 text-newblue-30"
      case "Regulation and Policy":
        return "border-swell-greendark text-swell-greendark"
      case "Stablecoins":
        return "border-newblue-50 text-newblue-50"
      case "Custody":
        return "border-newblue-30 text-newblue-30"
      case "Technology Innovation / AI":
        return "border-swell-orange text-swell-orange"
      case "Security and Compliance":
        return "border-newblue-50 text-newblue-50"
      case "Sustainability":
        return "border-swell-greendark text-swell-greendark"
      default:
        return ""
    }
  }

  return (
    <>
      <div className="hidden col-span-1 p-4 my-2 lg:col-span-4 md:block">
        <h2 className="text-gray-60 body3 lg:body2 xl:body1">{data.time}</h2>
      </div>
      <div
        className={clsx(
          "col-span-2 md:col-span-3 lg:col-span-8 my-2",
          toggleOpen ? "rounded-lg bg-gray-5" : "",
        )}
      >
        {data.description ? (
          <button
            className={clsx(
              "group flex justify-between items-center flex-grow-0 flex-shrink-0 w-full rounded-lg p-4 lg:px-6 text-gray-90",
              toggleOpen && "rounded-b-none",
              toggleOpen ? "" : "hover:bg-gray-5",
            )}
            onClick={() => setToggleOpen(!toggleOpen)}
            type="button"
          >
            <div>
              <p className="block mb-1 caption3 md:hidden text-gray-60">
                {data.time}
              </p>
              <h3
                className={clsx(
                  "ease-in-out md:mr-0 mr-4 heading3 md:heading2 xl:heading1 text-gray-90",
                )}
                itemProp="name"
              >
                {data.title}
              </h3>
            </div>
            <div
              className={clsx(
                "relative flex-grow-0 flex-shrink-0",
                toggleOpen && "rotate-45",
              )}
            >
              <svg
                width="25"
                height="25"
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                preserveAspectRatio="none"
              >
                <rect
                  width="15"
                  height="2"
                  transform="matrix(0.000136303 1 -1 0.000137483 13.0011 4.50195)"
                  fill="#000"
                />
                <rect
                  width="15"
                  height="2"
                  transform="matrix(-1 0.000137694 0.000136092 1 19.502 11.001)"
                  fill="#000"
                />
              </svg>
            </div>
          </button>
        ) : (
          <div
            className={clsx(
              "group justify-between items-center w-full rounded-lg p-4 lg:px-6",
            )}
          >
            <p className="block mb-1 caption3 md:hidden text-gray-60">
              {data.time}
            </p>
            <h3
              className={clsx(
                "ease-in-out md:mr-0 mr-4 heading3 md:heading2 xl:heading1 text-gray-90",
              )}
              itemProp="name"
            >
              {data.title}
            </h3>
          </div>
        )}
        <div>
          <span
            className={clsx(
              "p-4 pt-0 lg:px-6 body3",
              toggleOpen ? "block" : "hidden",
            )}
            itemProp="text"
          >
            <p className="xl:body2 lg:body3 body4 text-gray-90">
              {data.description}
            </p>
            {data.speakersCollection && (
              <div className="grid grid-cols-1 mt-8 md:grid-cols-2">
                {data.speakersCollection?.items?.map((speaker) => (
                  <BioModalTrigger
                    key={speaker.name}
                    modalOpen
                    name={speaker.name}
                    title={speaker.title}
                    bio={speaker.biography}
                    linkedin={speaker.linkedIn}
                    company={speaker.company}
                    lightmode
                  >
                    <div className="flex items-center mb-2">
                      {speaker.bioImage && (
                        <Image
                          className="object-cover w-[50px] h-[50px] flex-shrink-0 self-start mr-4 rounded-lg"
                          src={speaker.bioImage.url}
                          width="50"
                          height="50"
                          alt={speaker.name}
                        />
                      )}
                      <div className="text-gray-90">
                        <p className="body4">{speaker.name}</p>
                        <p className="leading-2.5 caption3">
                          {speaker.title}, {speaker.company}
                        </p>
                      </div>
                    </div>
                  </BioModalTrigger>
                ))}
              </div>
            )}
            {data.categories && (
              <div className="flex flex-wrap mt-8">
                {data.categories.map((cat) => (
                  <Pills
                    className={clsx(
                      "flex justify-center mr-2 h-9 mb-2 border-2",
                      getBackgroundColor(cat),
                    )}
                    key={cat}
                  >
                    <span className={clsx("flex items-center caption2")}>
                      {cat}
                    </span>
                  </Pills>
                ))}
              </div>
            )}
          </span>
        </div>
      </div>
    </>
  )
}

export default AgendaExpand
